import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trash, Minus, Plus, Tag } from "@phosphor-icons/react";
import { api, errorMessage, inr, formatLabel } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

export default function CartPage() {
  const { items, update, remove, coupon, setCoupon } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState(coupon);
  const [quote, setQuote] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!items.length) { setQuote(null); return; }
    let cancelled = false;
    api.post("/cart/quote", { items: items.map(({ book_id, format, qty }) => ({ book_id, format, qty })), coupon_code: coupon || null })
      .then(({ data }) => { if (!cancelled) { setQuote(data); setErr(""); } })
      .catch((e) => { if (!cancelled) { setErr(errorMessage(e)); if (coupon) setCoupon(""); } });
    return () => { cancelled = true; };
  }, [items, coupon, setCoupon]);

  const applyCoupon = async () => {
    try {
      const { data } = await api.post("/cart/quote", { items: items.map(({ book_id, format, qty }) => ({ book_id, format, qty })), coupon_code: code || null });
      setQuote(data); setCoupon(data.coupon_code || ""); setErr("");
      if (data.coupon_code) toast.success(`Coupon ${data.coupon_code} applied`);
    } catch (e) { toast.error(errorMessage(e)); }
  };

  if (!items.length) {
    return (
      <div className="mx-auto max-w-3xl px-5 py-28 text-center" data-testid="cart-empty">
        <p className="eyebrow">Your bag</p>
        <h1 className="mt-3 text-4xl font-medium tracking-tight">Nothing here yet.</h1>
        <Link to="/#shelf" className="mt-8 inline-block rounded-full bg-foreground px-7 py-3.5 text-sm font-semibold text-white" data-testid="cart-browse-link">Browse the shelf</Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-5 py-12 sm:px-8 md:py-20" data-testid="cart-page">
      <p className="eyebrow">Your bag</p>
      <h1 className="mt-3 text-4xl font-medium tracking-tight">{items.length} {items.length === 1 ? "title" : "titles"}</h1>
      <div className="mt-12 grid gap-12 md:grid-cols-12">
        <ul className="space-y-6 md:col-span-7">
          {items.map((it) => (
            <li key={it.book_id + it.format} className="flex gap-5 rounded-2xl bg-white p-4 ring-1 ring-border/70" data-testid={`cart-item-${it.book_id}-${it.format}`}>
              <img src={it.cover_url} alt={it.title} className="h-28 w-20 rounded object-cover shadow" />
              <div className="flex flex-1 flex-col">
                <Link to={`/books/${it.slug}`} className="font-display text-lg font-medium leading-snug hover:text-primary">{it.title}</Link>
                <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{formatLabel(it.format)}</p>
                <div className="mt-auto flex items-center justify-between pt-3">
                  {it.format !== "ebook" ? (
                    <div className="flex items-center rounded-full border border-border">
                      <button onClick={() => update(it.book_id, it.format, it.qty - 1)} className="p-2" data-testid={`cart-qty-decrease-${it.book_id}`}><Minus size={14} /></button>
                      <span className="w-6 text-center text-sm font-semibold" data-testid={`cart-qty-${it.book_id}`}>{it.qty}</span>
                      <button onClick={() => update(it.book_id, it.format, Math.min(it.stock || 99, it.qty + 1))} className="p-2" data-testid={`cart-qty-increase-${it.book_id}`}><Plus size={14} /></button>
                    </div>
                  ) : <span className="text-xs text-muted-foreground">1 licence</span>}
                  <div className="flex items-center gap-4">
                    <span className="font-semibold">{inr(it.unit_price * it.qty)}</span>
                    <button onClick={() => remove(it.book_id, it.format)} className="text-muted-foreground hover:text-destructive" data-testid={`cart-remove-${it.book_id}-${it.format}`}><Trash size={18} /></button>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
        <aside className="md:col-span-5">
          <div className="sticky top-24 rounded-3xl bg-white p-6 ring-1 ring-border/70" data-testid="cart-summary">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Tag className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="Coupon code" className="h-11 w-full rounded-full border border-input bg-muted/50 pl-9 pr-3 text-sm uppercase outline-none ring-primary focus:ring-2" data-testid="coupon-input" />
              </div>
              <button onClick={applyCoupon} className="rounded-full border border-foreground/20 px-4 text-sm font-semibold hover:bg-muted" data-testid="apply-coupon-button">Apply</button>
            </div>
            {err && <p className="mt-3 text-sm text-destructive" data-testid="cart-error">{err}</p>}
            <dl className="mt-6 space-y-2.5 text-sm">
              <div className="flex justify-between"><dt className="text-muted-foreground">Subtotal</dt><dd data-testid="cart-subtotal">{inr(quote?.subtotal)}</dd></div>
              {quote?.discount > 0 && <div className="flex justify-between text-accent"><dt>Discount ({quote.coupon_code})</dt><dd data-testid="cart-discount">− {inr(quote.discount)}</dd></div>}
              <div className="flex justify-between"><dt className="text-muted-foreground">Shipping</dt><dd data-testid="cart-shipping">{quote?.has_physical ? (quote.shipping === 0 ? "Free" : inr(quote.shipping)) : "—"}</dd></div>
              <div className="flex justify-between text-xs text-muted-foreground"><dt>Includes GST</dt><dd>{inr(quote?.tax)}</dd></div>
              <div className="flex justify-between border-t border-border pt-3 text-base font-semibold"><dt>Total</dt><dd data-testid="cart-total">{inr(quote?.total)}</dd></div>
            </dl>
            <button onClick={() => navigate(user ? "/checkout" : "/login?next=/checkout")} disabled={!quote} className="mt-6 w-full rounded-full bg-primary py-3.5 text-sm font-semibold text-white transition-[transform,background-color] hover:-translate-y-0.5 hover:bg-[#A94D31] disabled:opacity-40" data-testid="checkout-button">
              {user ? "Continue to checkout" : "Sign in to checkout"}
            </button>
            {!user && <p className="mt-3 text-center text-xs text-muted-foreground">An account keeps every order and invoice in one place.</p>}
          </div>
        </aside>
      </div>
    </div>
  );
}
